import { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { getMatchMinute, isUpcoming, isUpcomingWithin3Hours, getCompetitionPriority } from '../utils/homeHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function usePersonalizedFeed() {
  const heroRef = useRef(null);
  const watchButtonRef = useRef(null);
  const [heroContent, setHeroContent] = useState(null);
  const [heroType, setHeroType] = useState('');
  const [heroBackgroundSource, setHeroBackgroundSource] = useState('thumbnail');
  const [heroSlides, setHeroSlides] = useState([]);
  const [liveContent, setLiveContent] = useState([]);
  const [featuredReplays, setFeaturedReplays] = useState([]);
  const [fullMatchReplays, setFullMatchReplays] = useState([]);
  const [highlightsContent, setHighlightsContent] = useState([]);
  const [editorialContent, setEditorialContent] = useState([]);
  const [recommendedContent, setRecommendedContent] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matchMinutes, setMatchMinutes] = useState({});
  const [liveScores, setLiveScores] = useState({});
  const [showGlow, setShowGlow] = useState(false);
  const [previousHeroType, setPreviousHeroType] = useState('');
  const [followedTeams, setFollowedTeams] = useState([]);
  
  // Use a ref to always have fresh followedTeams in callbacks
  const followedTeamsRef = useRef([]);
  followedTeamsRef.current = followedTeams;

  useEffect(() => {
    fetchFollowedTeams();
  }, []);

  useEffect(() => {
    fetchContent();
    const pollInterval = setInterval(fetchContent, 30000);
    return () => clearInterval(pollInterval);
  }, [followedTeams]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchFollowedTeams = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/user/following`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setFollowedTeams((res.data.teams || []).map(t => t.toLowerCase()));
    } catch {
      // Not logged in or no followed teams
    }
  };

  const matchesFollowedTeam = useCallback((content) => {
    const teams = followedTeamsRef.current;
    if (!teams.length || !content.teams) return false;
    const teamsStr = content.teams.toLowerCase();
    return teams.some(t => teamsStr.includes(t));
  }, []);

  // AUTO FOCUS LIVE MATCH
  useEffect(() => {
    if (heroType === 'live' && previousHeroType !== 'live' && previousHeroType !== '') {
      if (heroRef.current) {
        heroRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      setShowGlow(true);
      setTimeout(() => setShowGlow(false), 3000);
    }
    setPreviousHeroType(heroType);
  }, [heroType, previousHeroType]);

  // Update match minutes every 30 seconds for live matches
  useEffect(() => {
    if (liveContent.length === 0) return;
    
    const updateMinutes = () => {
      const newMinutes = {};
      liveContent.forEach(match => {
        newMinutes[match.id] = getMatchMinute(match.start_time);
      });
      setMatchMinutes(newMinutes);
    };
    
    updateMinutes();
    const interval = setInterval(updateMinutes, 30000);
    return () => clearInterval(interval);
  }, [liveContent]);

  const fetchContent = async () => {
    try {
      const [featuredResponse, liveResponse, vodResponse, recsResponse, tickerResponse, compsResponse] = await Promise.all([
        axios.get(`${API}/content/featured`),
        axios.get(`${API}/content/live`),
        axios.get(`${API}/content/vod`),
        axios.get(`${API}/content/recommended/personalized`, {
          withCredentials: true,
          headers: localStorage.getItem('token') ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {},
        }),
        axios.get(`${API}/ticker/live`).catch(() => ({ data: { matches: [] } })),
        axios.get(`${API}/competitions`).catch(() => ({ data: { competitions: [] } }))
      ]);
      
      // Build live scores map from ticker
      const tickerMatches = tickerResponse.data?.matches || [];
      const scoresMap = {};
      for (const tm of tickerMatches) {
        if (tm.content_id && tm.status === 'live') {
          scoresMap[tm.content_id] = {
            home_score: tm.home_score,
            away_score: tm.away_score,
            match_minute: tm.match_minute,
          };
        }
      }
      setLiveScores(scoresMap);

      // Competitions from API
      const compsData = compsResponse.data?.competitions || compsResponse.data || [];
      setCompetitions(Array.isArray(compsData) ? compsData : []);
      
      const featuredContent = featuredResponse.data;
      const liveContentData = liveResponse.data;
      const vodContent = vodResponse.data || [];
      const recsData = recsResponse.data || [];
      setRecommendedContent(recsData);
      const featuredIds = new Set(featuredContent.map(c => c.id));
      const additionalLive = liveContentData.filter(c => !featuredIds.has(c.id));
      const content = [...additionalLive, ...featuredContent];

      // 1. LIVE content
      const live = content
        .filter(c => c.is_live)
        .sort((a, b) => {
          if (a.hero_priority !== b.hero_priority) {
            return (a.hero_priority || 999) - (b.hero_priority || 999);
          }
          const aFollowed = matchesFollowedTeam(a) ? 0 : 1;
          const bFollowed = matchesFollowedTeam(b) ? 0 : 1;
          if (aFollowed !== bFollowed) return aFollowed - bFollowed;
          if ((b.views || 0) !== (a.views || 0)) return (b.views || 0) - (a.views || 0);
          if ((b.viewer_count || 0) !== (a.viewer_count || 0)) return (b.viewer_count || 0) - (a.viewer_count || 0);
          return new Date(a.start_time || 0) - new Date(b.start_time || 0);
        });
      setLiveContent(live);

      // 2. Upcoming matches (within 12 hours)
      const upcoming = content
        .filter(c => !c.is_live && isUpcoming(c.start_time))
        .sort((a, b) => {
          if (a.hero_priority !== b.hero_priority) {
            return (a.hero_priority || 999) - (b.hero_priority || 999);
          }
          return new Date(a.start_time) - new Date(b.start_time);
        });

      // 3. Featured Replays — EXCLUDE YouTube content from hero-eligible pool
      const replays = content
        .filter(c => 
          !c.is_live && 
          !isUpcoming(c.start_time) &&
          (!c.start_time || new Date(c.start_time) < new Date()) &&
          c.content_type !== 'highlight' &&
          c.source !== 'youtube'
        )
        .sort((a, b) => {
          if (a.hero_priority !== b.hero_priority) {
            return (a.hero_priority || 999) - (b.hero_priority || 999);
          }
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        })
        .slice(0, 8);
      setFeaturedReplays(replays);

      // 4. Editorial override
      const editorialOverride = content.find(c => c.hero_priority === 1 && !c.is_live);

      // 5. Featured highlight — best recent YouTube highlight for hero fallback
      const highlightCandidates = [...content.filter(c =>
        !c.is_live && (c.category === 'highlight' || c.content_type === 'highlight')
      ), ...recsData.filter(c =>
        c.source === 'youtube' && c.category === 'highlight'
      )];
      // Deduplicate and sort by published_at (newest first)
      const hlSeen = new Set();
      const featuredHighlight = highlightCandidates.find(c => {
        if (hlSeen.has(c.id)) return false;
        hlSeen.add(c.id);
        return c.thumbnail_url; // must have a thumbnail
      }) || null;

      // HERO CAROUSEL — build up to 3 slides
      const followedLive = live.filter(c => matchesFollowedTeam(c));
      const upcomingSoon = upcoming.filter(c => isUpcomingWithin3Hours(c.start_time));
      const followedUpcomingSoon = upcomingSoon.filter(c => matchesFollowedTeam(c));

      const slides = [];
      const usedIds = new Set();

      // Slide 1: Live match or upcoming (<3h)
      const liveHero = followedLive[0] || live[0];
      const upcomingHero = followedUpcomingSoon[0] || upcomingSoon[0];
      if (liveHero) {
        slides.push({ content: liveHero, type: 'live', bgSource: liveHero.team_hero_image ? 'team' : 'match' });
        usedIds.add(liveHero.id);
      } else if (upcomingHero) {
        slides.push({ content: upcomingHero, type: 'starting', bgSource: 'competition' });
        usedIds.add(upcomingHero.id);
      }

      // Slide 2: Featured highlight
      if (featuredHighlight && !usedIds.has(featuredHighlight.id)) {
        slides.push({ content: featuredHighlight, type: 'highlight', bgSource: 'thumbnail' });
        usedIds.add(featuredHighlight.id);
      }

      // Slide 3: Top competition promo
      const compsArr = Array.isArray(compsData) ? compsData : [];
      const topComp = compsArr.find(c => c.logo_url || c.hero_image_url);
      if (topComp) {
        slides.push({
          content: {
            id: topComp.slug || topComp.id || topComp.name,
            title: topComp.name,
            teams: '',
            competition_name: topComp.name,
            thumbnail_url: topComp.hero_image_url || 'https://images.unsplash.com/photo-1720261558340-817c215d1c00?w=1600&q=80',
            logo_url: topComp.logo_url || '',
            description: topComp.description || `Watch ${topComp.name} live and on-demand.`,
            slug: topComp.slug,
          },
          type: 'competition',
          bgSource: 'thumbnail',
        });
      }

      // Add second live match if available and room
      if (live.length > 1 && slides.length < 3) {
        const secondLive = live.find(c => !usedIds.has(c.id));
        if (secondLive) {
          slides.splice(1, 0, { content: secondLive, type: 'live', bgSource: 'match' });
          usedIds.add(secondLive.id);
        }
      }

      // Fallback
      if (slides.length === 0) {
        const fallback = editorialOverride || replays[0] || content[0];
        if (fallback) slides.push({ content: fallback, type: fallback.is_live ? 'live' : 'replay', bgSource: 'match' });
      }

      const finalSlides = slides.slice(0, 3);
      setHeroSlides(finalSlides);

      // Backward-compat single hero (first slide)
      const selectedHero = finalSlides[0]?.content || null;
      const selectedType = finalSlides[0]?.type || '';
      const bgSource = finalSlides[0]?.bgSource || 'thumbnail';

      setHeroContent(selectedHero);
      setHeroType(selectedType);
      setHeroBackgroundSource(bgSource);

      const heroId = selectedHero?.id;

      const sortWithFollowed = (a, b) => {
        const aFollowed = matchesFollowedTeam(a) ? 0 : 1;
        const bFollowed = matchesFollowedTeam(b) ? 0 : 1;
        if (aFollowed !== bFollowed) return aFollowed - bFollowed;
        const pa = getCompetitionPriority(a.competition_name);
        const pb = getCompetitionPriority(b.competition_name);
        if (pa !== pb) return pa - pb;
        return new Date(a.start_time || 0) - new Date(b.start_time || 0);
      };

      setLiveContent(live.filter(c => c.id !== heroId).sort(sortWithFollowed));
      setFeaturedReplays(
        replays.filter(c => c.id !== heroId).sort(sortWithFollowed).slice(0, 8)
      );

      // Full Match Replays: combine featured replays + VOD match replays, deduplicated
      // Exclude highlights, originals, documentaries, and YouTube content from replay pool
      const isMatchReplay = (c) =>
        c.category !== 'highlight' &&
        c.content_type !== 'highlight' &&
        c.category !== 'original' &&
        c.category !== 'documentary' &&
        c.source !== 'youtube';
      const replayPool = replays.filter(c => c.id !== heroId && isMatchReplay(c));
      const vodMatchReplays = vodContent.filter(c => c.id !== heroId && isMatchReplay(c));
      const seenIds = new Set(replayPool.map(c => c.id));
      const merged = [...replayPool, ...vodMatchReplays.filter(c => !seenIds.has(c.id))];
      const sortedReplays = merged
        .sort((a, b) => {
          const aF = matchesFollowedTeam(a) ? 0 : 1;
          const bF = matchesFollowedTeam(b) ? 0 : 1;
          if (aF !== bF) return aF - bF;
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        })
        .slice(0, 8);
      setFullMatchReplays(sortedReplays);

      // Highlights: from featured/live pool + YouTube highlights from recommendations
      // Sort by published_at first (newer YouTube content), then created_at
      const baseHighlights = content.filter(c =>
        c.id !== heroId && (c.category === 'highlight' || c.content_type === 'highlight')
      );
      const ytHighlights = recsData.filter(c =>
        c.id !== heroId &&
        c.source === 'youtube' &&
        c.category === 'highlight' &&
        !baseHighlights.some(bh => bh.id === c.id)
      );
      const allHighlights = [...baseHighlights, ...ytHighlights]
        .sort((a, b) => {
          const dateA = a.published_at || a.created_at || '';
          const dateB = b.published_at || b.created_at || '';
          return new Date(dateB) - new Date(dateA);
        })
        .slice(0, 6);
      setHighlightsContent(allHighlights);
      setEditorialContent(
        content.filter(c =>
          c.id !== heroId && (c.category === 'original' || c.category === 'documentary')
        ).slice(0, 6)
      );
      setUpcomingMatches(
        upcoming.filter(c => c.id !== heroId).sort(sortWithFollowed)
      );

    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTimeUntilKickoff = (startTime) => {
    if (!startTime) return '';
    const kickoff = new Date(startTime);
    const now = new Date();
    const diffMs = kickoff - now;
    if (diffMs <= 0) return 'Starting now';
    const minutes = Math.floor(diffMs / 60000);
    return `${minutes}min`;
  };

  // Feed intelligence — tells the homepage what's available for dynamic ordering
  const feedIntelligence = {
    hasLive: liveContent.length > 0,
    hasFollowed: followedTeams.length > 0,
    followedTeamNames: followedTeams,
    liveCount: liveContent.length,
    upcomingCount: upcomingMatches.length,
  };

  return {
    heroRef,
    watchButtonRef,
    heroContent,
    heroType,
    heroBackgroundSource,
    heroSlides,
    liveContent,
    featuredReplays,
    fullMatchReplays,
    recommendedContent,
    competitions,
    highlightsContent,
    editorialContent,
    upcomingMatches,
    loading,
    matchMinutes,
    liveScores,
    showGlow,
    followedTeams,
    matchesFollowedTeam,
    getTimeUntilKickoff,
    feedIntelligence,
  };
}
